import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';

export interface FollowListData {
  event: NostrEvent | null;
  pubkeys: string[];
}

/**
 * The feed is always viewed through the lens of this hardcoded account.
 * npub1hee433872q2gen90cqh2ypwcq9z7y5ugn23etrd2l2rrwpruss8qwmrsv6
 */
export const FEED_LENS_PUBKEY =
  'be7358c4fe50148cccafc02ea205d80145e253889aa3958daafa8637047c840e';

/**
 * Queries the hardcoded lens account's NIP-02 contact list (kind 3) and
 * extracts the pubkeys of every account they follow. Used as the `authors`
 * filter for the main feed.
 *
 * The list is re-fetched periodically so the feed stays up to date when the
 * lens account's follow list changes. No login is required.
 *
 * Inspired by Ditto's `useFollowList` pattern.
 */
export function useFollowList() {
  const { nostr } = useNostr();

  return useQuery<FollowListData>({
    queryKey: ['follow-list', FEED_LENS_PUBKEY],
    queryFn: async ({ signal }) => {
      const events = await nostr.query(
        [{ kinds: [3], authors: [FEED_LENS_PUBKEY], limit: 1 }],
        { signal: AbortSignal.any([signal, AbortSignal.timeout(5000)]) },
      );

      // Take the most recently published kind 3 event as authoritative.
      const event = events.sort((a, b) => b.created_at - a.created_at)[0];
      if (!event) return { event: null, pubkeys: [] };

      const pubkeys = Array.from(
        new Set(
          event.tags
            .filter(([name, value]) => name === 'p' && typeof value === 'string')
            .map(([, pk]) => pk),
        ),
      );

      return { event, pubkeys };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
